"use client";

import React, {
  useCallback,
  useContext,
  useMemo,
  FC,
  useState,
  useRef,
} from "react";

import { useIsAuthenticated } from "@azure/msal-react";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import clsx from "clsx";
import { useParams } from "next/navigation";

import { GlobalPathParams } from "~/app/[locale]/types";
import { BreadcrumbsItem } from "~/bff/types/BreadcrumbsItem";
import { Product } from "~/bff/types/Product";
import { Store } from "~/bff/types/Store";
import { Variant } from "~/bff/types/Variant";
import {
  DataLayerData,
  DataLayerItemListId,
  DataLayerItemListName,
  EventsNames,
  EventTypes,
  NewProductDataLayerData,
} from "~/constants/data-layer";
import { getCurrencyCodeByLocale } from "~/constants/i18n";
import { DictionaryLabelContext } from "~/context/dictionary-label/context";
import { DictionaryKeys } from "~/context/dictionary-label/types";
import { NotificationContext } from "~/context/notification/context";
import { ShoppingListContext } from "~/context/shopping-list/context";
import { checkIsOneSizeProduct } from "~/helpers/check-is-one-size-product";
import { getCountryCode } from "~/helpers/country/get-country-code";
import {
  getDisplayPriceFromVariants,
  getDisplayPriceWithoutCurrency,
  getPriceWithFixedPoint,
} from "~/helpers/get-display-price";
import { getGAClearanceAttributes } from "~/helpers/get-ga-clearance-attributes";
import { getItemListNameForAddToWishlist } from "~/helpers/get-item-list-name-for-add-to-wishlist";
import { getLineItemIdentity } from "~/helpers/get-line-item-identity/index";
import { getPageCategory } from "~/helpers/get-page-categories-titles";
import { pushDataLayer } from "~/helpers/push-data-layer";
import { Evergage } from "~/lib/salesforce/evergage";
import {
  EvergageActions,
  EvergageItemActions,
} from "~/lib/salesforce/evergage/actions";
import { AzureConfigurator } from "~/services/azure-configurator/azure-configurator";
import { Favorite24 } from "~/theme/icons/components/favorite";
import { FavoriteSelected24 } from "~/theme/icons/components/favorite-selected";
import { Nullable } from "~/types/general.types";

import { clickAndCollectOverallStatusGA } from "../product-availability-block/helpers/click-and-collect-overall-status-ga";
import { clickAndCollectProductStatusGA } from "../product-availability-block/helpers/click-and-collect-product-status-ga";
import { clickAndCollectSkuStatusGA } from "../product-availability-block/helpers/click-and-collect-sku-status-ga";
import { clickAndCollectStoreStatusGA } from "../product-availability-block/helpers/click-and-collect-store-status-ga";
import { inStoreStatusGA } from "../product-availability-block/helpers/in-store-status-ga";
import { getVariantsFromProductInfo } from "../sales-panel/helpers";

import { TooltipStyled } from "./styled";

export interface GoogleAnalyticsData {
  breadcrumbs?: Nullable<BreadcrumbsItem>[] | undefined;
  product?: Product;
  position?: number;
  status?: string;
  categorySlug?: string;
  type?: EventTypes;
  itemListName?: DataLayerItemListName;
  itemListId?: DataLayerItemListId;
  priceWithClearance?: {
    isRange?: boolean;
    regular?: Nullable<number>;
    clearance?: Nullable<number>;
  };
}

export interface HeartIconProps {
  evergageId?: string;
  className?: string;
  isPdp?: boolean;
  productSku?: string;
  sizeIsSelected?: boolean;
  hasText?: boolean;
  translations?: {
    addButtonText?: string;
    addedButtonText?: string;
  };
  evergageProductData?: {
    _id: Nullable<string> | undefined;
    name: Nullable<string> | undefined;
    color: Nullable<string> | undefined;
  };
  googleAnalyticsData?: GoogleAnalyticsData;
  currentStore?: Nullable<Store>;
  productName?: string;
}

export const HeartIcon: FC<HeartIconProps> = ({
  evergageId,
  evergageProductData = {},
  className,
  isPdp = false,
  productSku,
  sizeIsSelected = false,
  hasText = false,
  translations: { addButtonText, addedButtonText } = {},
  googleAnalyticsData,
  currentStore,
  productName,
}) => {
  const {
    shoppingListSkuMap,
    handleAddProductToShoppingList,
    handleRemoveProductFromShoppingList,
    userShoppingListId,
  } = useContext(ShoppingListContext) ?? {};
  const notificationContext = useContext(NotificationContext);

  const [isShoppingListBtnDisabled, setIsShoppingListBtnDisabled] = useState(false);

  const isAuthenticated = useIsAuthenticated();
  const iconButtonRef = useRef<HTMLButtonElement>(null);

  const dictionary = useContext(DictionaryLabelContext);
  const { locale } = useParams<GlobalPathParams>();
  const isClearancePricingEnabled = useMemo(
    () =>
      AzureConfigurator.getConfig(locale)?.featureFlagList?.clearancePricing
        ?.enabled,
    [locale],
  );

  // This is a problem???
  const isProductInShoppingList = useMemo(
    () =>
      shoppingListSkuMap?.has(
        getLineItemIdentity(productSku ?? "", sizeIsSelected.toString()),
      ),
    [productSku, shoppingListSkuMap, sizeIsSelected],
  );

  const productIdFromShoppingList = shoppingListSkuMap?.get(
    getLineItemIdentity(productSku ?? "", sizeIsSelected.toString()),
  )?.id;

  const handleShowItemAddedNotification = useCallback(() => {
    notificationContext?.handleShowNotification?.(
      dictionary?.[DictionaryKeys.ItemAddedToShoppingListMsg],
    );
  }, [notificationContext, dictionary]);

  const tooltipText = isProductInShoppingList
    ? dictionary?.[DictionaryKeys.ShoppingListTooltip] ||
      "Saved items are managed in Shopping list"
    : "";

  const calculateEvent = useCallback(() => {
    const country = getCountryCode(locale);
    const {
      product,
      position,
      status,
      type,
      itemListName,
      itemListId,
      priceWithClearance,
    } = googleAnalyticsData || {};
    const categorySlug = googleAnalyticsData?.categorySlug;
    const {
      pageCategory,
      pageSubCategory,
      pageSubSubCategory,
      pageSubSubSubCategory,
    } = getPageCategory(categorySlug);

    const currency = getCurrencyCodeByLocale(locale);

    const isOneSize = checkIsOneSizeProduct(product?.sizeId ?? "");
    const priceForOneSize =
      typeof product?.masterPrice === "string"
        ? getDisplayPriceWithoutCurrency(product?.masterPrice)
        : product?.masterPrice;

    const price = isOneSize
      ? priceForOneSize
      : getDisplayPriceWithoutCurrency(
          getDisplayPriceFromVariants({
            variants: product?.variants as Variant[],
            masterVariant: {
              price: product?.masterPrice ?? "",
              isActive: product?.isProductActive ?? undefined,
            },
            locale: locale,
            currencyCode: getCurrencyCodeByLocale(locale),
            fromLabel: dictionary?.[DictionaryKeys.FromLabel],
          }),
        );
    const priceForGA = price ? +price : undefined;
    const variants = product ? getVariantsFromProductInfo(product) : [];
    const size = isOneSize
      ? product?.displaySize
      : sizeIsSelected &&
        variants?.find(({ sku }) => sku === productSku)?.displaySize;

    return {
      [DataLayerData.event]: EventsNames.addToWishList,
      [DataLayerData.siteCountry]: country,
      [DataLayerData.type]: type ?? EventTypes.shoppingList,
      [DataLayerData.pageCategory]: pageCategory || "Search",
      [DataLayerData.pageSubCategory]: pageSubCategory,
      [DataLayerData.pageSubSubCategory]: pageSubSubCategory,
      [DataLayerData.loggedInStatus]: isAuthenticated,
      [DataLayerData.storeSelected]: currentStore?.geomodifier ?? undefined,
      [DataLayerData.ecommerce]: {
        [DataLayerData.currency]: currency,
        [DataLayerData.value]: isClearancePricingEnabled
          ? getPriceWithFixedPoint(
              priceWithClearance?.clearance || priceWithClearance?.regular,
            )
          : priceForGA,
        [DataLayerData.itemListId]: itemListId ?? undefined,
        [DataLayerData.itemListName]:
          itemListName ??
          getItemListNameForAddToWishlist(
            pageCategory,
            pageSubCategory,
            pageSubSubCategory,
            pageSubSubSubCategory,
          ),
        [DataLayerData.items]: [
          {
            [NewProductDataLayerData.itemName]: product?.name,
            [NewProductDataLayerData.itemId]: product?.productCode,
            [NewProductDataLayerData.index]: position || 1,
            [NewProductDataLayerData.itemBrand]:
              dictionary?.[DictionaryKeys.BrandName],
            [NewProductDataLayerData.itemCategory]: pageCategory,
            [NewProductDataLayerData.itemCategory2]: pageSubCategory,
            [NewProductDataLayerData.itemCategory3]: pageSubSubCategory,
            [NewProductDataLayerData.itemCategory4]: pageSubSubSubCategory,
            [NewProductDataLayerData.itemCategory5]: clickAndCollectOverallStatusGA(
              product,
              currentStore,
            ),
            [NewProductDataLayerData.dimension49]:
              clickAndCollectStoreStatusGA(currentStore),
            ...(isPdp && {
              [NewProductDataLayerData.dimension50]: clickAndCollectSkuStatusGA(
                product,
                sizeIsSelected,
                productSku,
              ),
            }),
            [NewProductDataLayerData.dimension51]: clickAndCollectOverallStatusGA(
              product,
              currentStore,
            ),
            ...(isPdp &&
              sizeIsSelected && {
                [NewProductDataLayerData.dimension52]: inStoreStatusGA(
                  status,
                  currentStore,
                ),
              }),
            [NewProductDataLayerData.dimension53]:
              clickAndCollectProductStatusGA(product),
            [NewProductDataLayerData.price]: price ? +price : undefined,
            ...(isClearancePricingEnabled
              ? getGAClearanceAttributes({
                  regular: priceWithClearance?.regular,
                  clearance: priceWithClearance?.clearance,
                })
              : {}),
            [NewProductDataLayerData.quantity]: 1,
            [NewProductDataLayerData.dimension2]: size || undefined,
            [NewProductDataLayerData.variant]: product?.displayColor,
          },
        ],
      },
    };
  }, [
    googleAnalyticsData,
    locale,
    currentStore,
    dictionary,
    isAuthenticated,
    productSku,
    sizeIsSelected,
    isPdp,
    isClearancePricingEnabled,
  ]);

  const handleIconClick = useCallback(
    (e: React.BaseSyntheticEvent) => {
      e.stopPropagation();
      e.preventDefault();

      setIsShoppingListBtnDisabled(true);

      if (!isPdp && isProductInShoppingList) {
        setIsShoppingListBtnDisabled(false);
        return;
      }

      const dataForGA = calculateEvent();

      const addProductRequestVariables = {
        sku: productSku,
        sizeIsSelected,
        shoppingListId: userShoppingListId,
      };

      if (isPdp && !isProductInShoppingList) {
        handleAddProductToShoppingList?.(addProductRequestVariables)
          .then(() => {
            handleShowItemAddedNotification();
            pushDataLayer(dataForGA);
          })
          .catch(() => {
            return;
          })
          .finally(() => {
            setIsShoppingListBtnDisabled(false);
          });
        return;
      }

      if (isPdp && isProductInShoppingList) {
        handleRemoveProductFromShoppingList?.({
          shoppingListId: userShoppingListId,
          removableItemId: productIdFromShoppingList,
          sizeIsSelected,
          sku: productSku,
        }).finally(() => {
          setIsShoppingListBtnDisabled(false);
        });
        return;
      }

      handleAddProductToShoppingList?.(addProductRequestVariables)
        .then(() => {
          handleShowItemAddedNotification();
          Evergage.sendEvent(locale, {
            action: EvergageActions.ADD_TO_SHOPPING_LIST,
            itemAction: EvergageItemActions.FAVORITE,
            catalog: {
              Product: {
                ...evergageProductData,
              },
            },
          });
          pushDataLayer(dataForGA);
        })
        .catch(() => {
          return;
        })
        .finally(() => {
          setIsShoppingListBtnDisabled(false);
          setTimeout(() => {
            iconButtonRef.current?.focus();
          }, 0);
        });
    },
    [
      isPdp,
      isProductInShoppingList,
      productSku,
      sizeIsSelected,
      userShoppingListId,
      handleAddProductToShoppingList,
      handleShowItemAddedNotification,
      handleRemoveProductFromShoppingList,
      productIdFromShoppingList,
      evergageProductData,
      locale,
      calculateEvent,
    ],
  );

  const ariaLabel = useMemo(() => {
    const productNameString = productName ? ` ${productName}` : "";
    return isProductInShoppingList
      ? `remove from shopping list${productNameString}`
      : `add to shopping list${productNameString}`;
  }, [isProductInShoppingList, productName]);

  const iconComponent = useMemo(() => {
    return isProductInShoppingList ? (
      <FavoriteSelected24 title="remove from shopping list" />
    ) : (
      <Favorite24 title="add to shopping list" />
    );
  }, [isProductInShoppingList]);

  const evergageProps = useMemo(
    () => ({
      "data-evergage": evergageId,
      "data-evergage-action": isProductInShoppingList,
    }),
    [isProductInShoppingList, evergageId],
  );

  if (hasText) {
    return (
      <Button
        variant="text"
        startIcon={iconComponent}
        onClick={handleIconClick}
        className={clsx(className, "underlined")}
        sx={{ cursor: "pointer" }}
        aria-label={ariaLabel}
        {...evergageProps}
        disabled={isShoppingListBtnDisabled}
      >
        {isProductInShoppingList ? addedButtonText : addButtonText}
      </Button>
    );
  }

  return isPdp ? (
    <IconButton
      onClick={handleIconClick}
      className={className}
      aria-label={ariaLabel}
      {...evergageProps}
      size="large"
      sx={{ cursor: "pointer" }}
      disabled={isShoppingListBtnDisabled}
    >
      {iconComponent}
    </IconButton>
  ) : (
    <TooltipStyled
      title={tooltipText}
      placement="top-start"
      enterTouchDelay={0}
      leaveTouchDelay={5000}
      arrow
      describeChild
      id={`tooltip-description-${productSku}`}
      slotProps={{ popper: { keepMounted: true } }}
    >
      <IconButton
        ref={iconButtonRef}
        onClick={handleIconClick}
        className={className}
        aria-label={ariaLabel}
        size="large"
        sx={{ cursor: "pointer" }}
        disabled={isShoppingListBtnDisabled}
        aria-describedby={`tooltip-description-${productSku}`}
      >
        {iconComponent}
      </IconButton>
    </TooltipStyled>
  );
};
